import { colors } from '../../setup/theme';
import { percentage, round } from '../number/number';

const allowedTags = new Set(['b', 'br', 'i', 'li', 'p', 'span', 'u', 'ul']);
const allowedSpanClasses = new Set(Object.keys(colors).map(color => `text-${color}`));

export function formatMetricValue(unit: string, value?: number) {
    const val = unit === '%' ? percentage(value) : round(value);
    const formatted = val.toLocaleString('en-EN');
    return `${formatted}${unit}`;
}

export function formatValue(unit: string, value?: number) {
    const val = value || 0;
    const sign = val < 0 ? '-' : '';
    const formatted = Math.abs(round(val)).toLocaleString('en-EN');
    return `${sign}${unit}${formatted}`;
}

export function getMetricValue(metric: Metric, impact: number, cost: number) {
    return cost ? getProfitValue(metric, impact, cost) / cost : 0;
}

export function getProfitValue(metric: Metric, impact: number, cost: number) {
    const costMultiplier = metric.costType === metric.impactType ? 1 : 0;
    return impact - cost * costMultiplier;
}

export function parseHtml(html: string, variables?: Variables) {
    // Prepare html
    html = html.trim();

    // Parse variables
    if (variables) {
        html = parseVariables(html, variables);
    }

    // Parse colors
    Object.keys(colors).forEach(color => {
        html = html.replaceAll(`<${color}>`, `<span class="text-${color}">`).replaceAll(`</${color}>`, `</span>`);
    });

    return sanitizeHtml(html);
}

export function parseVariables(html: string, variables: Variables) {
    // Get variables
    const vars = Object.entries(variables);
    if (!vars.length) {
        return html;
    }

    const keys = vars.map(([key]) => key);
    const knownVariableRegex = new RegExp(`{(${keys.map(escapeRegex).join('|')})}`);
    const seenHtml = new Set<string>();
    const maxIterations = Math.max(20, keys.length * 10);
    let parsedHtml = html;
    let iterations = 0;

    while (parsedHtml.includes('{') && knownVariableRegex.test(parsedHtml) && iterations < maxIterations) {
        if (seenHtml.has(parsedHtml)) {
            break;
        }
        seenHtml.add(parsedHtml);

        vars.forEach(([key, value]) => {
            parsedHtml = parsedHtml.replaceAll(`{${key}}`, String(value));
        });

        iterations += 1;
    }

    return parsedHtml;
}

function escapeRegex(value: string) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function sanitizeHtml(html: string) {
    let sanitized = html
        .replace(/<!--[\s\S]*?-->/g, '')
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');

    sanitized = sanitized.replace(/<\/?([a-zA-Z0-9-]+)([^>]*)>/g, (tag, rawTagName, rawAttributes) => {
        const tagName = String(rawTagName).toLowerCase();
        const closing = tag.startsWith('</');

        if (!allowedTags.has(tagName)) {
            return '';
        }

        if (closing) {
            return tagName === 'br' ? '' : `</${tagName}>`;
        }

        if (tagName === 'span') {
            const classMatch = String(rawAttributes).match(/\bclass\s*=\s*(['"])([^'"]+)\1/i);
            const className = classMatch?.[2]
                .split(/\s+/)
                .find(value => allowedSpanClasses.has(value));
            return className ? `<span class="${className}">` : '<span>';
        }

        return tagName === 'br' ? '<br />' : `<${tagName}>`;
    });

    return sanitized;
}
